import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { videoService, imageService } from '../services/api';

// Types
interface Video {
  id: string;
  title: string;
  description: string;
  url: string;
  thumbnail: string;
  duration: string;
  category: string;
  date_publication: string;
  heure_publication: string;
}

interface Image {
  id: string;
  title: string;
  description: string;
  url: string;
  category: string;
  date_publication: string;
  heure_publication?: string;
}

interface MediaItem {
  id: string;
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
  duration?: string;
  category: string;
  date_publication: string;
  heure_publication?: string;
  type: 'video' | 'photo';
}

// URL de base de l'API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aikash.ci';

// Fonction pour construire l'URL complète des images
const buildImageUrl = (url: string): string => {
  // Si l'URL commence par /uploads/, c'est une image uploadée
  if (url.startsWith('/uploads/')) {
    return `${API_BASE_URL}${url}`;
  }
  // Sinon, c'est une image statique du frontend
  return url;
};

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Composants stylisés modernes
const MainContent = styled.main`
  flex: 1;
  margin-top: 80px;
  min-height: calc(100vh - 80px);
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f0f9ff 50%, #fef3f2 75%, #fefce8 100%);
  
  @media (max-width: 768px) {
    margin-top: 60px;
  }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #475569 75%, #64748b 100%);
  color: white;
  padding: 120px 20px 100px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.15"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.15"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.15"/><circle cx="10" cy="60" r="0.8" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.6" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.4;
  }
  
  @media (max-width: 768px) {
    padding: 80px 15px 60px;
  }
  
  @media (max-width: 480px) {
    padding: 60px 10px 40px;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  margin-bottom: 30px;
  text-shadow: 0 4px 20px rgba(0,0,0,0.3);
  background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #ec4899 75%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${fadeInUp} 1s ease-out;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: clamp(1.1rem, 2.5vw, 1.4rem);
  opacity: 0.95;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.7;
  font-weight: 300;
  animation: ${fadeInUp} 1s ease-out 0.2s both;
  
  @media (max-width: 768px) {
    max-width: 90%;
    margin-bottom: 30px;
    font-size: clamp(1rem, 4vw, 1.2rem);
  }
  
  @media (max-width: 480px) {
    max-width: 95%;
    margin-bottom: 25px;
    font-size: 1rem;
  }
`;

const HeroStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  flex-wrap: wrap;
  animation: ${fadeInUp} 1s ease-out 0.4s both;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const HeroStat = styled.div`
  text-align: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  min-width: 120px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    min-width: 100px;
    padding: 15px;
  }
`;

const HeroStatNumber = styled.div`
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeroStatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  
  @media (max-width: 1200px) {
    max-width: 100%;
    padding: 0 25px;
  }
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
  
  @media (max-width: 480px) {
    padding: 0 15px;
  }
`;

const GallerySection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 25%, #f1f5f9 50%, #f8fafc 75%, #ffffff 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to bottom, #f0f9ff, #ffffff);
  }
  
  @media (max-width: 768px) {
    padding: 60px 0;
    
    &::before {
      height: 60px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 40px 0;
    
    &::before {
      height: 40px;
    }
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  color: #0f172a;
  margin-bottom: 20px;
  font-weight: 800;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #ec4899 75%, #f59e0b 100%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: clamp(1.8rem, 5vw, 2.5rem);
    margin-bottom: 15px;
    
    &::after {
      width: 80px;
      height: 3px;
      bottom: -10px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: clamp(1.5rem, 6vw, 2rem);
    margin-bottom: 12px;
    
    &::after {
      width: 60px;
      height: 2px;
      bottom: -8px;
    }
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: #64748b;
  font-size: 1.1rem;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 40px;
    max-width: 90%;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 30px;
    max-width: 95%;
    line-height: 1.5;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  margin-bottom: 60px;
  animation: ${slideInLeft} 1s ease-out 0.6s both;
  
  @media (max-width: 768px) {
    gap: 20px;
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    margin-bottom: 30px;
  }
`;

const SearchFiltersRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  align-items: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  padding: 25px;
  border-radius: 25px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 15px 40px rgba(0,0,0,0.08);
  
  @media (max-width: 768px) {
    padding: 20px;
    gap: 15px;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 15px;
    gap: 10px;
    border-radius: 15px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const DateSearchContainer = styled.div<{ active: boolean }>`
  display: flex;
  gap: 15px;
  align-items: center;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)'};
  padding: 20px;
  border-radius: 20px;
  border: 2px solid ${props => props.active ? '#10b981' : '#e2e8f0'};
  box-shadow: ${props => props.active 
    ? '0 8px 25px rgba(16, 185, 129, 0.25)' 
    : '0 8px 25px rgba(0,0,0,0.08)'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.active 
      ? '0 12px 35px rgba(16, 185, 129, 0.3)' 
      : '0 12px 35px rgba(0,0,0,0.12)'};
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 10px;
    border-radius: 12px;
    flex-direction: column;
    align-items: stretch;
    
    &:hover {
      transform: none;
    }
  }
`;

const DateInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 15px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: #f8fafc;
  
  &:focus {
    outline: none;
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
    background: white;
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const DateSearchButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%);
  color: white;
  border: none;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  box-shadow: 0 8px 25px rgba(6, 182, 212, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(6, 182, 212, 0.4);
    background: linear-gradient(135deg, #0891b2 0%, #0e7490 50%, #155e75 100%);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const ClearDateButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 50%, #dc2626 100%);
  color: white;
  border: none;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  box-shadow: 0 8px 25px rgba(249, 115, 22, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(249, 115, 22, 0.4);
    background: linear-gradient(135deg, #ea580c 0%, #dc2626 50%, #b91c1c 100%);
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
  padding: 20px;
  border-radius: 25px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 8px 25px rgba(0,0,0,0.06);
  
  @media (max-width: 768px) {
    padding: 15px;
    gap: 12px;
    border-radius: 20px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    gap: 8px;
    border-radius: 15px;
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLabel = styled.span`
  font-weight: 700;
  color: #475569;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? '#8b5cf6' : '#e2e8f0'};
  border-radius: 25px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)'};
  color: ${props => props.active ? 'white' : '#475569'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.9rem;
  box-shadow: ${props => props.active 
    ? '0 12px 30px rgba(139, 92, 246, 0.4)' 
    : '0 4px 15px rgba(0,0,0,0.08)'};
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.active 
      ? '0 18px 40px rgba(139, 92, 246, 0.5)' 
      : '0 10px 30px rgba(0,0,0,0.15)'};
    background: ${props => props.active 
      ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 50%, #5b21b6 100%)' 
      : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)'};
  }
  
  &:active {
    transform: translateY(-1px);
  }
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 40px;
  margin-top: 60px;
  animation: ${fadeInUp} 1s ease-out 0.8s both;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 35px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 30px;
    margin-top: 40px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 25px;
    margin-top: 30px;
  }
`;

const MediaCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 2px solid #f1f5f9;
  position: relative;
  
  &:hover {
    transform: translateY(-15px) scale(1.02);
    box-shadow: 0 35px 90px rgba(0,0,0,0.15);
    border-color: #8b5cf6;
    background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 50%, #e0f2fe 100%);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #ec4899 75%, #f59e0b 100%);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
  
  @media (max-width: 768px) {
    border-radius: 20px;
    
    &:hover {
      transform: translateY(-8px) scale(1.01);
      box-shadow: 0 25px 60px rgba(0,0,0,0.12);
    }
  }
  
  @media (max-width: 480px) {
    border-radius: 15px;
    
    &:hover {
      transform: translateY(-5px) scale(1.005);
      box-shadow: 0 20px 50px rgba(0,0,0,0.1);
    }
  }
`;

const MediaThumbnail = styled.div`
  width: 100%;
  height: 280px;
  position: relative;
  overflow: hidden;
  background: #f0f0f0;
  
  /* Overlay seulement pour les images, pas pour les vidéos */
  ${MediaCard}:hover &::before {
    opacity: 1;
  }
  
  /* Overlay pour les images */
  &:has(img)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(30, 41, 59, 0.1), rgba(71, 85, 105, 0.3));
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 2;
  }
  
  @media (max-width: 768px) {
    height: 240px;
  }
  
  @media (max-width: 480px) {
    height: 200px;
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${MediaCard}:hover & {
    transform: scale(1.05);
  }
`;

const ThumbnailFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f0f0, #e0e0e0);
  color: #666;
  font-size: 3rem;
  font-weight: bold;
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.9) 0%, rgba(59, 130, 246, 0.9) 50%, rgba(139, 92, 246, 0.9) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
  pointer-events: none; // Permet de cliquer à travers l'overlay
  
  ${MediaCard}:hover & {
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: all 0.3s ease;
  box-shadow: 0 15px 40px rgba(0,0,0,0.25);
  
  &:hover {
    transform: scale(1.15);
    box-shadow: 0 20px 50px rgba(0,0,0,0.35);
  }
`;

const MediaContent = styled.div`
  padding: 30px;
  
  @media (max-width: 768px) {
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const MediaTitle = styled.h3`
  font-size: 1.3rem;
  color: #1e293b;
  margin-bottom: 15px;
  font-weight: 700;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 10px;
  }
`;

const MediaDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
  font-size: 1rem;
  margin-bottom: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 15px;
    -webkit-line-clamp: 2;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 12px;
    line-height: 1.5;
  }
`;

const MediaMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-direction: column;
    align-items: flex-start;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const MediaDate = styled.span`
  font-style: italic;
  color: #94a3b8;
  font-size: 0.9rem;
  font-weight: 500;
`;

const MediaCategory = styled.span`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #bae6fd 100%);
  color: #0c4a6e;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.8rem;
  border: 1px solid #0ea5e9;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.15);
`;

const MediaType = styled.span<{ type: 'video' | 'photo' }>`
  background: ${props => props.type === 'video' 
    ? 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)' 
    : 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)'};
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.8rem;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  border: 1px solid ${props => props.type === 'video' ? 'rgba(6, 182, 212, 0.3)' : 'rgba(245, 158, 11, 0.3)'};
`;

const Duration = styled.span`
  background: rgba(0,0,0,0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.8rem;
  position: absolute;
  bottom: 15px;
  right: 15px;
  backdrop-filter: blur(10px);
`;

const VideoIndicator = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(59, 130, 246, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: ${pulse} 2s ease-in-out infinite;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 25px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.08);
  margin-top: 60px;
  border: 2px dashed #e2e8f0;
`;

const NoResultsIcon = styled.div`
  font-size: 5rem;
  margin-bottom: 30px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const NoResultsTitle = styled.h3`
  font-size: 1.8rem;
  color: #1e293b;
  margin-bottom: 20px;
  font-weight: 700;
`;

const NoResultsText = styled.p`
  color: #64748b;
  font-size: 1.1rem;
  max-width: 500px;
  margin: 0 auto;
  line-height: 1.6;
`;

const StatsSection = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #475569 75%, #64748b 100%);
  color: white;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to bottom, #f0f9ff, #0f172a);
  }
  
  /* Ajout d'un motif de points subtil */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="white" opacity="0.1"/><circle cx="80" cy="80" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="50" r="0.8" fill="white" opacity="0.1"/><circle cx="10" cy="70" r="0.6" fill="white" opacity="0.1"/><circle cx="90" cy="30" r="0.6" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
    opacity: 0.3;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 60px 0;
    
    &::before {
      height: 60px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 40px 0;
    
    &::before {
      height: 40px;
    }
  }
`;

const StatsTitle = styled.h2`
  text-align: center;
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  color: white;
  margin-bottom: 30px;
  font-weight: 800;
  position: relative;
  text-shadow: 0 4px 20px rgba(0,0,0,0.5);
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 5px;
    background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #ec4899 75%, #f59e0b 100%);
    border-radius: 3px;
    box-shadow: 0 4px 15px rgba(6, 182, 212, 0.4);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 25px;
    
    &::after {
      width: 100px;
      height: 4px;
      bottom: -15px;
    }
  }
`;

const StatsSubtitle = styled.p`
  text-align: center;
  color: #f1f5f9;
  font-size: 1.2rem;
  margin-bottom: 80px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  font-weight: 400;
  text-shadow: 0 2px 10px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 60px;
    max-width: 600px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 50px;
  margin-top: 80px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 40px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 30px;
    margin-top: 60px;
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 50px 40px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0.05) 100%);
  border-radius: 30px;
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  /* Effet de brillance au hover */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transform: rotate(45deg) translateX(-100%);
    transition: transform 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-15px) scale(1.02);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.1) 100%);
    border-color: rgba(6, 182, 212, 0.8);
    box-shadow: 0 30px 70px rgba(0, 0, 0, 0.25);
  }
  
  &:hover::before {
    transform: rotate(45deg) translateX(100%);
  }
  
  @media (max-width: 768px) {
    padding: 40px 30px;
    min-height: 180px;
  }
`;

const StatNumber = styled.div`
  font-size: 4rem;
  font-weight: 900;
  background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 25%, #8b5cf6 50%, #ec4899 75%, #f59e0b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 25px;
  text-shadow: 0 4px 20px rgba(0,0,0,0.3);
  filter: drop-shadow(0 2px 10px rgba(6, 182, 212, 0.3));
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 3rem;
    margin-bottom: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 1.3rem;
  color: #f8fafc;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 2px 10px rgba(0,0,0,0.4);
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    letter-spacing: 1.5px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 60vh;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #60a5fa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1.2rem;
  color: #64748b;
  font-weight: 500;
`;

// Données d'exemple pour les images (à remplacer par l'API)
const imagesData: Image[] = [
  {
    id: '1',
    title: "Championnat National 2024",
    description: "Moments forts du championnat national de karaté Shotokan 2024",
    url: "/karate1.webp",
    category: "Compétitions",
    date_publication: "2024-12-15"
  },
  {
    id: '2',
    title: "Stage de Formation des Maîtres",
    description: "Séance de formation intensive pour les maîtres de karaté",
    url: "/karate2.jpg",
    category: "Formation",
    date_publication: "2024-12-22"
  },
  {
    id: '3',
    title: "Cérémonie de Remise de Grades",
    description: "Cérémonie traditionnelle de remise de grades et ceintures",
    url: "/karate3.webp",
    category: "Cérémonies",
    date_publication: "2024-12-28"
  },
  {
    id: '4',
    title: "Entraînement des Jeunes",
    description: "Séance d'entraînement spécialement conçue pour les jeunes",
    url: "/karate1.webp",
    category: "Entraînements",
    date_publication: "2024-12-10"
  },
  {
    id: '5',
    title: "Démonstration de Kata",
    description: "Démonstration exceptionnelle de kata par nos experts",
    url: "/karate2.jpg",
    category: "Démonstrations",
    date_publication: "2024-12-05"
  },
  {
    id: '6',
    title: "Séminaire International",
    description: "Séminaire international avec des maîtres du monde entier",
    url: "/karate3.webp",
    category: "Événements",
    date_publication: "2024-12-01"
  }
];

const categories = [
  { id: 'all', name: 'Toutes les catégories' },
  { id: 'Compétitions', name: 'Compétitions' },
  { id: 'Formation', name: 'Formation' },
  { id: 'Cérémonies', name: 'Cérémonies' },
  { id: 'Entraînements', name: 'Entraînements' },
  { id: 'Démonstrations', name: 'Démonstrations' },
  { id: 'Événements', name: 'Événements' }
];

const mediaTypes = [
  { id: 'all', name: 'Tous les médias' },
  { id: 'video', name: 'Vidéos' },
  { id: 'photo', name: 'Images' }
];

function GalleryPage() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [images, setImages] = useState<Image[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMediaType, setSelectedMediaType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchDate, setSearchDate] = useState('');
  const [filteredByDate, setFilteredByDate] = useState(false);

  // Charger les vidéos et images depuis l'API
  useEffect(() => {
    const loadMedia = async () => {
      try {
        const [videosData, imagesData] = await Promise.all([
          videoService.getAllVideos(),
          imageService.getAllImages()
        ]);
        
        // Traiter les vidéos pour construire les URLs complètes
        const processedVideos = videosData.map((video: any) => {
          console.log('🔍 Traitement vidéo:', video);
          
          // Construire l'URL complète de la vidéo
          let videoUrl = video.url;
          if (video.url && video.url.startsWith('/uploads/')) {
            videoUrl = `${process.env.REACT_APP_API_URL || 'https://aikash.ci'}${video.url}`;
            console.log('✅ URL vidéo construite:', videoUrl);
          } else {
            console.log('⚠️ URL vidéo non standard:', video.url);
          }
          
          // Construire l'URL de la miniature
          let thumbnailUrl = video.thumbnail;
          if (video.thumbnail && video.thumbnail.startsWith('/uploads/')) {
            // Essayer d'abord l'URL complète
            thumbnailUrl = `${process.env.REACT_APP_API_URL || 'https://aikash.ci'}${video.thumbnail}`;
            console.log('✅ URL thumbnail construite:', thumbnailUrl);
          } else if (!video.thumbnail) {
            // Utiliser un thumbnail par défaut générique
            thumbnailUrl = 'https://via.placeholder.com/300x200/666666/ffffff?text=Video';
            console.log('🖼️ Thumbnail par défaut utilisé');
          } else {
            console.log('⚠️ URL thumbnail non standard:', video.thumbnail);
            // Utiliser un thumbnail par défaut en cas d'URL non standard
            thumbnailUrl = 'https://via.placeholder.com/300x200/666666/ffffff?text=Video';
          }
          
          return {
            ...video,
            url: videoUrl,
            thumbnail: thumbnailUrl
          };
        });
        
        setVideos(processedVideos);
        setImages(imagesData);
      } catch (error) {
        console.error('Erreur lors du chargement des médias:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMedia();
  }, []);

  // Combiner et filtrer les médias
  const allMedia: MediaItem[] = [
    ...videos.map(video => ({
      ...video,
      type: 'video' as const,
      heure_publication: video.heure_publication
    })),
    ...images.map(image => ({
      ...image,
      type: 'photo' as const,
      thumbnail: buildImageUrl(image.url), // Utiliser l'URL complète
      heure_publication: image.heure_publication
    }))
  ];

  const filteredMedia = allMedia.filter(media => {
    const categoryMatch = selectedCategory === 'all' || media.category === selectedCategory;
    const typeMatch = selectedMediaType === 'all' || media.type === selectedMediaType;
    
    // Filtrage par date si une date est sélectionnée
    let dateMatch = true;
    if (searchDate && filteredByDate) {
      const mediaDate = new Date(media.date_publication);
      const searchDateObj = new Date(searchDate);
      dateMatch = mediaDate.toDateString() === searchDateObj.toDateString();
    }
    
    return categoryMatch && typeMatch && dateMatch;
  });

  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [showMediaModal, setShowMediaModal] = useState(false);

  const handleMediaClick = (media: MediaItem) => {
    setSelectedMedia(media);
    setShowMediaModal(true);
  };

  const handleDateSearch = () => {
    if (searchDate) {
      setFilteredByDate(true);
    }
  };

  const handleClearDateSearch = () => {
    setSearchDate('');
    setFilteredByDate(false);
  };

  const formatDate = (dateString: string, heurePublication?: string) => {
    const date = new Date(dateString);
    const dateFormatted = date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    if (heurePublication) {
      return `${dateFormatted} à ${heurePublication}`;
    }
    
    return dateFormatted;
  };

  if (loading) {
    return (
      <MainContent>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Chargement de la galerie...</LoadingText>
        </LoadingContainer>
      </MainContent>
    );
  }

  return (
    <MainContent>
      <HeroSection>
        <Container>
          <HeroTitle>🎥📸 Galerie Média</HeroTitle>
          <HeroSubtitle>
            Explorez notre collection de vidéos et photos : compétitions, entraînements, 
            cérémonies et moments inoubliables du karaté traditionnel
          </HeroSubtitle>
          <HeroStats>
            <HeroStat>
              <HeroStatNumber>{videos.length}</HeroStatNumber>
              <HeroStatLabel>Vidéos</HeroStatLabel>
            </HeroStat>
            <HeroStat>
              <HeroStatNumber>{images.length}</HeroStatNumber>
              <HeroStatLabel>Images</HeroStatLabel>
            </HeroStat>
            <HeroStat>
              <HeroStatNumber>{allMedia.length}</HeroStatNumber>
              <HeroStatLabel>Total</HeroStatLabel>
            </HeroStat>
          </HeroStats>
        </Container>
      </HeroSection>

      <GallerySection>
        <Container>
          <SectionTitle>Notre Galerie</SectionTitle>
          <SectionSubtitle>
            Découvrez les moments les plus mémorables de notre club de karaté Shotokan
          </SectionSubtitle>
          
          <FiltersContainer>
            <SearchFiltersRow>
              <FilterGroup>
                <FilterLabel>Type :</FilterLabel>
                {mediaTypes.map(type => (
                  <FilterButton
                    key={type.id}
                    active={selectedMediaType === type.id}
                    onClick={() => setSelectedMediaType(type.id)}
                  >
                    {type.name}
                  </FilterButton>
                ))}
              </FilterGroup>
              
              <FilterGroup>
                <FilterLabel>Catégorie :</FilterLabel>
                {categories.map(category => (
                  <FilterButton
                    key={category.id}
                    active={selectedCategory === category.id}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {category.name}
                  </FilterButton>
                ))}
              </FilterGroup>
            </SearchFiltersRow>
            
            <DateSearchContainer active={filteredByDate}>
              <FilterLabel>Rechercher par date :</FilterLabel>
              <DateInput
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                placeholder="Sélectionner une date"
              />
              <DateSearchButton onClick={handleDateSearch}>
                🔍 Rechercher
              </DateSearchButton>
              {filteredByDate && (
                <ClearDateButton onClick={handleClearDateSearch}>
                  ✕ Effacer
                </ClearDateButton>
              )}
            </DateSearchContainer>
          </FiltersContainer>

          {filteredMedia.length > 0 ? (
            <>
              <MediaGrid>
                {filteredMedia.map(media => (
                  <MediaCard 
                    key={media.id} 
                    onClick={(e) => {
                      // Empêcher complètement l'ouverture du modal pour les vidéos
                      if (media.type === 'video') {
                        e.preventDefault();
                        e.stopPropagation();
                        return; // Ne rien faire pour les vidéos
                      }
                      // Ouvrir le modal seulement pour les images
                      handleMediaClick(media);
                    }}
                  >
                    <MediaThumbnail>
                                            {media.type === 'video' ? (
                        // Pour les vidéos : permettre la lecture directe
                        <video
                          src={media.url}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            cursor: 'pointer',
                            borderRadius: '0',
                            backgroundColor: '#000'
                          }}
                          muted
                          preload="metadata"
                          controls
                          onLoadStart={() => {
                            console.log('🎬 Chargement vidéo:', media.url);
                          }}
                          onLoadedMetadata={(e) => {
                            const video = e.target as HTMLVideoElement;
                            console.log('✅ Métadonnées vidéo chargées:', {
                              duration: video.duration,
                              width: video.videoWidth,
                              height: video.videoHeight
                            });
                          }}
                          onError={(e) => {
                            console.error('❌ Erreur de chargement vidéo:', media.url);
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            const fallback = target.parentElement?.querySelector('.video-fallback') as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('🎬 Clic sur la vidéo - modal bloqué');
                          }}
                        />
                      ) : (
                        // Pour les images : utiliser le thumbnail ou l'URL de l'image
                        <ThumbnailImage
                          src={media.thumbnail || buildImageUrl(media.url)}
                          alt={media.title}
                          onError={(e) => {
                            console.error('❌ Erreur de chargement thumbnail:', media.thumbnail);
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.parentElement?.querySelector('.thumbnail-fallback') as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                          onLoad={() => {
                            console.log('✅ Thumbnail chargé avec succès:', media.thumbnail);
                          }}
                        />
                      )}
                      
                      
                      {/* Fallback pour les images en cas d'erreur */}
                      {media.type === 'photo' && (
                        <ThumbnailFallback 
                          className="thumbnail-fallback"
                          style={{ display: 'none' }}
                        >
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', marginBottom: '10px' }}>
                              📸
                            </div>
                            <div style={{ fontSize: '0.9rem', color: '#666' }}>
                              Image
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
                              {media.title.substring(0, 20)}...
                            </div>
                          </div>
                        </ThumbnailFallback>
                      )}
                      
                      {/* Badge "Vidéo" pour indiquer que c'est cliquable */}
                      {media.type === 'video' && (
                        <VideoIndicator>🎬 Vidéo</VideoIndicator>
                      )}
                      
                      {/* Afficher la durée seulement si elle est valide */}
                      {media.type === 'video' && media.duration && media.duration !== '00:00' && (
                        <Duration>{media.duration}</Duration>
                      )}
                      
                      {/* Overlay seulement pour les images, pas pour les vidéos */}
                      {media.type === 'photo' && (
                        <VideoOverlay>
                          <PlayButton>📸</PlayButton>
                        </VideoOverlay>
                      )}
                    </MediaThumbnail>
                    <MediaContent>
                      <MediaTitle>{media.title}</MediaTitle>
                      <MediaDescription>{media.description}</MediaDescription>
                      <MediaMeta>
                        <MediaDate>
                          {formatDate(media.date_publication, media.heure_publication)}
                        </MediaDate>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                          <MediaCategory>{media.category}</MediaCategory>
                          <MediaType type={media.type}>
                            {media.type === 'video' ? '🎥' : '📸'}
                          </MediaType>
                        </div>
                      </MediaMeta>
                    </MediaContent>
                  </MediaCard>
                ))}
              </MediaGrid>
            </>
          ) : (
            <NoResults>
              <NoResultsIcon>🔍</NoResultsIcon>
              <NoResultsTitle>Aucun média trouvé</NoResultsTitle>
              <NoResultsText>
                Aucun média ne correspond aux filtres sélectionnés. 
                Essayez de modifier vos critères de recherche.
              </NoResultsText>
            </NoResults>
          )}
        </Container>
      </GallerySection>

      <StatsSection>
        <Container>
          <StatsTitle>Statistiques de la Galerie</StatsTitle>
          <StatsSubtitle>
            Un aperçu de notre collection de médias et de son évolution
          </StatsSubtitle>
          <StatsGrid>
            <StatCard>
              <StatNumber>{videos.length}</StatNumber>
              <StatLabel>Vidéos</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{images.length}</StatNumber>
              <StatLabel>Images</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>{allMedia.length}</StatNumber>
              <StatLabel>Total Médias</StatLabel>
            </StatCard>
          </StatsGrid>
        </Container>
      </StatsSection>

      {/* Modal pour afficher les médias */}
      {showMediaModal && selectedMedia && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            position: 'relative'
          }}>
            {/* Bouton de fermeture */}
            <button
              onClick={() => setShowMediaModal(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                fontSize: '20px',
                cursor: 'pointer',
                zIndex: 1001,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>

            {/* Contenu du modal */}
            <div style={{ padding: '20px' }}>
              <h2 style={{ 
                margin: '0 0 15px 0', 
                color: '#333', 
                fontSize: '1.5rem',
                textAlign: 'center'
              }}>
                {selectedMedia.title}
              </h2>
              
              <p style={{ 
                margin: '0 0 20px 0', 
                color: '#666', 
                textAlign: 'center',
                fontSize: '1rem'
              }}>
                {selectedMedia.description}
              </p>

              {/* Affichage du média */}
              {selectedMedia.type === 'video' ? (
                <div style={{ 
                  width: '100%', 
                  maxWidth: '800px', 
                  margin: '0 auto',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}>
                  <video
                    src={selectedMedia.url}
                    poster={selectedMedia.thumbnail}
                    controls
                    autoPlay
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '70vh'
                    }}
                    onLoadStart={() => {
                      console.log('🎬 Début du chargement vidéo:', selectedMedia.url);
                    }}
                    onCanPlay={() => {
                      console.log('✅ Vidéo prête à être lue:', selectedMedia.url);
                    }}
                    onError={(e) => {
                      console.error('❌ Erreur de lecture vidéo:', e);
                      console.error('URL de la vidéo:', selectedMedia.url);
                      console.error('URL du poster:', selectedMedia.thumbnail);
                      const target = e.target as HTMLVideoElement;
                      target.style.display = 'none';
                      // Afficher un message d'erreur
                      const errorDiv = target.parentElement?.querySelector('.video-error') as HTMLElement;
                      if (errorDiv) errorDiv.style.display = 'block';
                    }}
                  >
                    Votre navigateur ne supporte pas la lecture de vidéos.
                  </video>
                  
                  {/* Message d'erreur en cas de problème de lecture */}
                  <div 
                    className="video-error"
                    style={{ 
                      display: 'none',
                      padding: '40px',
                      textAlign: 'center',
                      color: '#666',
                      background: '#f5f5f5',
                      borderRadius: '10px'
                    }}
                  >
                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
                    <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
                      Erreur de lecture vidéo
                    </h3>
                    <p style={{ margin: '0 0 15px 0' }}>
                      Impossible de lire cette vidéo. Vérifiez que le fichier est accessible.
                    </p>
                    <button
                      onClick={() => window.open(selectedMedia.url, '_blank')}
                      style={{
                        background: '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Ouvrir dans un nouvel onglet
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  width: '100%', 
                  maxWidth: '800px', 
                  margin: '0 auto',
                  textAlign: 'center'
                }}>
                  <img
                    src={buildImageUrl(selectedMedia.url)}
                    alt={selectedMedia.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      borderRadius: '10px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }}
                    onError={(e) => {
                      console.error('Erreur de chargement image:', e);
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      // Afficher un message d'erreur
                      const errorDiv = target.parentElement?.querySelector('.image-error') as HTMLElement;
                      if (errorDiv) errorDiv.style.display = 'block';
                    }}
                  />
                  
                  {/* Message d'erreur en cas de problème de chargement d'image */}
                  <div 
                    className="image-error"
                    style={{ 
                      display: 'none',
                      padding: '40px',
                      textAlign: 'center',
                      color: '#666',
                      background: '#f5f5f5',
                      borderRadius: '10px'
                    }}
                  >
                    <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⚠️</div>
                    <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
                      Erreur de chargement d'image
                    </h3>
                    <p style={{ margin: '0 0 15px 0' }}>
                      Impossible de charger cette image. Vérifiez que le fichier est accessible.
                    </p>
                    <button
                      onClick={() => window.open(buildImageUrl(selectedMedia.url), '_blank')}
                      style={{
                        background: '#2196F3',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Ouvrir dans un nouvel onglet
                    </button>
                  </div>
                </div>
              )}

              {/* Métadonnées */}
              <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: '#f8f9fa', 
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  <div>
                    <strong>Catégorie :</strong> {selectedMedia.category}
                  </div>
                  <div>
                    <strong>Date :</strong> {formatDate(selectedMedia.date_publication, selectedMedia.heure_publication)}
                  </div>
                  {selectedMedia.type === 'video' && selectedMedia.duration && (
                    <div>
                      <strong>Durée :</strong> {selectedMedia.duration}
                    </div>
                  )}
                </div>
                
                {/* Informations de débogage pour les vidéos */}
                {selectedMedia.type === 'video' && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: '#e3f2fd', 
                    borderRadius: '8px',
                    border: '1px solid #2196f3',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ marginBottom: '10px', fontWeight: 'bold', color: '#1976d2' }}>
                      🔍 Informations de débogage
                    </div>
                    <div style={{ textAlign: 'left', fontFamily: 'monospace' }}>
                      <div><strong>URL vidéo :</strong> {selectedMedia.url}</div>
                      <div><strong>URL thumbnail :</strong> {selectedMedia.thumbnail}</div>
                      <div><strong>ID :</strong> {selectedMedia.id}</div>
                    </div>
                    <button
                      onClick={() => {
                        console.log('🔍 Test de la vidéo:', selectedMedia);
                        console.log('🎬 URL complète:', selectedMedia.url);
                        console.log('🖼️ Thumbnail complète:', selectedMedia.thumbnail);
                        // Tester l'accessibilité de l'URL
                        fetch(selectedMedia.url, { method: 'HEAD' })
                          .then(response => {
                            console.log('✅ URL accessible:', response.status, response.statusText);
                          })
                          .catch(error => {
                            console.error('❌ URL non accessible:', error);
                          });
                      }}
                      style={{
                        background: '#2196f3',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        marginTop: '10px'
                      }}
                    >
                      🧪 Tester l'URL
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </MainContent>
  );
}

export default GalleryPage;
